import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Building2, BedDouble, Bath, DollarSign, Heart, LogOut, User, Settings, Eye, Share2, Map, SlidersHorizontal, X, ChevronDown, Bell, BellRing } from 'lucide-react';
import Logo from '@/components/Logo';
import ShareDialog from '@/components/ShareDialog';
import SEO from '@/components/SEO';
import ApartmentMap from '@/components/ApartmentMap';
import ListingCard from '@/components/ListingCard';
import SavedSearchModal from '@/components/SavedSearchModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { trackApartmentView, trackApartmentFavorite, trackMapView, trackFilterUsage } from '../utils/analytics';
import useAuthStore from '../store/authStore';


const Dashboard = () => {
  const navigate = useNavigate();
  // Use Zustand store for user state
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [savedSearchModalOpen, setSavedSearchModalOpen] = useState(false);
  const [savedSearchCount, setSavedSearchCount] = useState(0);
  
  // Track map view when it changes
  useEffect(() => {
    if (viewMode === 'map') {
      trackMapView();
    }
  }, [viewMode]);
  
  // Filters
  const [bedrooms, setBedrooms] = useState('');
  const [minRent, setMinRent] = useState('');
  const [maxRent, setMaxRent] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [state, setState] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [activeRecommendation, setActiveRecommendation] = useState('');

  // Count active filters
  const activeFilterCount = [bedrooms, minRent, maxRent, bathrooms, state, neighborhood].filter(Boolean).length;

  // Price preset options ($0 to $15,000 in $500 increments)
  const priceOptions = [
    { label: 'No Min', value: '' },
    { label: '$0', value: '0' },
    ...Array.from({ length: 30 }, (_, i) => {
      const price = (i + 2) * 500;
      return { label: `$${price.toLocaleString()}`, value: String(price) };
    }),
  ];
  const maxPriceOptions = [
    { label: 'No Max', value: '' },
    ...Array.from({ length: 30 }, (_, i) => {
      const price = (i + 2) * 500;
      return { label: `$${price.toLocaleString()}`, value: String(price) };
    }),
  ];

  // NYC neighborhoods grouped by borough
  const neighborhoodOptions = [
    { group: 'Manhattan', items: ['Chelsea', 'East Village', 'Financial District', 'Flatiron District', 'Harlem', 'Hudson Yards', 'Kips Bay', 'Midtown West', 'Murray Hill', 'SoHo', 'Tribeca', 'Turtle Bay', 'Upper East Side', 'Upper West Side', 'West Village'] },
    { group: 'Brooklyn', items: ['Brooklyn Heights', 'Carroll Gardens', 'DUMBO', 'Fort Greene', 'Gowanus', 'Prospect Heights', 'Williamsburg'] },
    { group: 'Queens', items: ['Jamaica', 'Long Island City'] },
    { group: 'New Jersey', items: ['Harrison', 'Jersey City'] },
  ];

  useEffect(() => {
    fetchUnits();
    fetchFavorites();
    fetchRecommendations();
    fetchSavedSearchCount();
  }, [bedrooms, minRent, maxRent, bathrooms, state, neighborhood]);

  const fetchSavedSearchCount = async () => {
    try {
      const response = await axios.get(`${API}/saved-searches`, { withCredentials: true });
      setSavedSearchCount(response.data.length);
    } catch (error) {
      console.error('Error fetching saved search count:', error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/recommendations`);
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  const applyRecommendation = (rec) => {
    // Clear existing filters first
    clearFilters();
    setActiveRecommendation(rec.id);
    
    // Apply the recommendation's filters
    if (rec.filter) {
      if (rec.filter.bedrooms !== undefined) setBedrooms(String(rec.filter.bedrooms));
      if (rec.filter.min_bedrooms !== undefined) setBedrooms(String(rec.filter.min_bedrooms));
      if (rec.filter.min_rent) setMinRent(String(rec.filter.min_rent));
      if (rec.filter.max_rent) setMaxRent(String(rec.filter.max_rent));
      if (rec.filter.sort) setSortBy(rec.filter.sort);
      if (rec.filter.new) setSortBy('newest');
    }
  };

  const fetchUnits = async () => {
    try {
      const params = new URLSearchParams();
      if (bedrooms) params.append('bedrooms', bedrooms);
      if (minRent) params.append('min_rent', minRent);
      if (maxRent) params.append('max_rent', maxRent);
      if (bathrooms) params.append('bathrooms', bathrooms);
      if (state) params.append('state', state);
      if (neighborhood) params.append('neighborhood', neighborhood);
      
      const response = await axios.get(`${API}/units?limit=500&${params.toString()}`, { withCredentials: true });
      
      // Distribute apartments from different buildings evenly to show variety
      const diversifiedUnits = diversifyListings(response.data);
      setUnits(diversifiedUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
      toast.error('Failed to load apartments');
    } finally {
      setLoading(false);
    }
  };

  const hasRealImages = (unit) => {
    // Check if unit has real images (not placeholder/unsplash)
    if (!unit.images || unit.images.length === 0) return false;
    const firstImage = unit.images[0];
    // Filter out Unsplash placeholders
    return !firstImage.includes('unsplash.com') && !firstImage.includes('photo-1556912173');
  };

  const diversifyListings = (units) => {
    // Separate featured units first
    const featuredUnits = units.filter(unit => unit.featured);
    const nonFeaturedUnits = units.filter(unit => !unit.featured);
    
    // Separate units with real images and without (only for non-featured)
    const unitsWithImages = nonFeaturedUnits.filter(hasRealImages);
    const unitsWithoutImages = nonFeaturedUnits.filter(unit => !hasRealImages(unit));

    // Group units with images by building
    const buildingGroups = {};
    unitsWithImages.forEach(unit => {
      const buildingId = unit.building?.id || 'unknown';
      if (!buildingGroups[buildingId]) {
        buildingGroups[buildingId] = [];
      }
      buildingGroups[buildingId].push(unit);
    });

    // Sort units within each building by price
    Object.keys(buildingGroups).forEach(buildingId => {
      buildingGroups[buildingId].sort((a, b) => a.rent - b.rent);
    });

    // Interleave units from different buildings (round-robin)
    const diversified = [];
    const buildingArrays = Object.values(buildingGroups);
    let hasMore = true;
    let index = 0;

    while (hasMore) {
      hasMore = false;
      buildingArrays.forEach(buildingUnits => {
        if (index < buildingUnits.length) {
          diversified.push(buildingUnits[index]);
          hasMore = true;
        }
      });
      index++;
    }

    // Prepend featured units at the top, then diversified units
    return [...featuredUnits, ...diversified];
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      const favIds = new Set(response.data.map(f => f.unit.id));
      setFavorites(favIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (unitId) => {
    try {
      if (favorites.has(unitId)) {
        await axios.delete(`${API}/favorites/${unitId}`, { withCredentials: true });
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(unitId);
          return newSet;
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/${unitId}`, {}, { withCredentials: true });
        setFavorites(prev => new Set(prev).add(unitId));
        toast.success('Added to favorites');
        
        // Track favorite addition
        const unit = units.find(u => u.id === unitId);
        if (unit) {
          trackApartmentFavorite(unitId, unit.building?.name || 'Unknown Building');
        }
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const clearFilters = () => {
    setBedrooms('');
    setMinRent('');
    setMaxRent('');
    setBathrooms('');
    setState('');
    setNeighborhood('');
    setSortBy('');
    setActiveRecommendation('');
  };

  // Recommendation icon mapping
  const getRecommendationIcon = (iconName) => {
    switch(iconName) {
      case 'building': return <Building2 className="w-4 h-4" />;
      case 'dollar': return <DollarSign className="w-4 h-4" />;
      case 'sparkle': return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/>
        </svg>
      );
      case 'crown': return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 2h14v2H5v-2z"/>
        </svg>
      );
      case 'bed': return <BedDouble className="w-4 h-4" />;
      case 'piggy': return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 10c0-1.1-.9-2-2-2V6c0-1.1-.9-2-2-2H9C7.9 4 7 4.9 7 6v2c-1.1 0-2 .9-2 2v3c0 .55.45 1 1 1h1v3c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2v-3h1c.55 0 1-.45 1-1v-3zM9 6h6v2H9V6z"/>
        </svg>
      );
      case 'users': return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
      );
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Browse No-Fee Apartments"
        description={`Search ${units.length}+ no broker fee apartments in NYC & NJ. Filter by bedrooms, price, and location. Real photos, verified listings, zero fees.`}
        keywords="browse no fee apartments, search NYC apartments, apartment listings, no broker fee search, NYC rentals, NJ apartments, no fee apartments near me, affordable apartments NYC, luxury no fee rentals, studio apartments no broker fee, 1 bedroom no fee NYC, pet friendly no fee apartments, doorman building no fee"
        url="/dashboard"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "No-Fee Apartment Listings",
          "description": "Browse verified no broker fee apartments",
          "numberOfItems": units.length,
          "itemListElement": units.slice(0, 10).map((unit, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Apartment",
              "name": `${unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} BR`} at ${unit.building?.name}`,
              "offers": {
                "@type": "Offer",
                "price": unit.rent,
                "priceCurrency": "USD"
              }
            }
          }))
        }}
      />
      
      {/* Header - Light Theme */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Logo size="default" variant="dark" />
            
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/fee-free-finds')}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 font-semibold"
              >
                #FeeFreeFinds
              </Button>
              
              <Button
                variant="ghost"
                onClick={() => navigate('/favorites')}
                data-testid="nav-favorites-btn"
                className="text-gray-600 hover:text-amber-600 hover:bg-gray-100"
              >
                <Heart className="w-5 h-5 mr-2" />
                Favorites
              </Button>
              
              {user?.is_admin && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin')}
                  data-testid="nav-admin-btn"
                  className="text-gray-600 hover:text-amber-600 hover:bg-gray-100"
                >
                  <Settings className="w-5 h-5 mr-2" />
                  Admin
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-btn" className="text-gray-600 hover:text-amber-600 hover:bg-gray-100">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-gray-200">
                  <DropdownMenuItem className="font-medium text-gray-900">{user?.name}</DropdownMenuItem>
                  <DropdownMenuItem className="text-sm text-gray-500">{user?.email}</DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-gray-700 hover:text-amber-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Responsive Filter Bar */}
        <div className="mb-6 space-y-4">
          {/* Desktop Filter Bar - Light Theme */}
          <div className="hidden md:flex flex-wrap items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            {/* Location */}
            <div className="flex items-center gap-2">
              <Map className="w-4 h-4 text-amber-600" />
              <Select value={state || "any"} onValueChange={(val) => { setState(val === "any" ? "" : val); if (val === "any") setNeighborhood(''); }}>
                <SelectTrigger data-testid="state-filter" className="w-[130px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="any" className="text-gray-900">All Areas</SelectItem>
                  <SelectItem value="NY" className="text-gray-900">New York</SelectItem>
                  <SelectItem value="NJ" className="text-gray-900">New Jersey</SelectItem>
                  <SelectItem value="PA" className="text-gray-900">Pennsylvania</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Neighborhood */}
            <Select value={neighborhood || "any-hood"} onValueChange={(val) => setNeighborhood(val === "any-hood" ? "" : val)}>
              <SelectTrigger data-testid="neighborhood-filter" className="w-[160px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                <SelectValue placeholder="Neighborhood" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                <SelectItem value="any-hood" className="text-gray-900">All Neighborhoods</SelectItem>
                {neighborhoodOptions.map(group => (
                  <React.Fragment key={group.group}>
                    <SelectItem value={`__group_${group.group}`} disabled className="text-xs font-bold text-amber-600 uppercase tracking-wider pt-2">
                      {group.group}
                    </SelectItem>
                    {group.items.map(hood => (
                      <SelectItem key={hood} value={hood} className="text-gray-900 pl-4">{hood}</SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            
            <div className="w-px h-6 bg-gray-300" />
            
            {/* Beds */}
            <div className="flex items-center gap-2">
              <BedDouble className="w-4 h-4 text-amber-600" />
              <Select value={bedrooms || "any"} onValueChange={(val) => setBedrooms(val === "any" ? "" : val)}>
                <SelectTrigger data-testid="bedrooms-filter" className="w-[100px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                  <SelectValue placeholder="Beds" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="any" className="text-gray-900">Any Beds</SelectItem>
                  <SelectItem value="0" className="text-gray-900">Studio</SelectItem>
                  <SelectItem value="1" className="text-gray-900">1 Bed</SelectItem>
                  <SelectItem value="2" className="text-gray-900">2 Beds</SelectItem>
                  <SelectItem value="3" className="text-gray-900">3+ Beds</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Baths */}
            <div className="flex items-center gap-2">
              <Bath className="w-4 h-4 text-amber-600" />
              <Select value={bathrooms || "any"} onValueChange={(val) => setBathrooms(val === "any" ? "" : val)}>
                <SelectTrigger data-testid="bathrooms-filter" className="w-[100px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                  <SelectValue placeholder="Baths" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="any" className="text-gray-900">Any Baths</SelectItem>
                  <SelectItem value="1" className="text-gray-900">1 Bath</SelectItem>
                  <SelectItem value="1.5" className="text-gray-900">1.5 Bath</SelectItem>
                  <SelectItem value="2" className="text-gray-900">2+ Baths</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-px h-6 bg-gray-300" />
            
            {/* Price Range - Dropdowns */}
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-600" />
              <Select value={minRent || "no-min"} onValueChange={(val) => setMinRent(val === "no-min" ? "" : val)}>
                <SelectTrigger data-testid="min-rent-filter" className="w-[110px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                  <SelectValue placeholder="No Min" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                  {priceOptions.map(opt => (
                    <SelectItem key={`min-${opt.value || 'none'}`} value={opt.value || "no-min"} className="text-gray-900">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-gray-400">—</span>
              <Select value={maxRent || "no-max"} onValueChange={(val) => setMaxRent(val === "no-max" ? "" : val)}>
                <SelectTrigger data-testid="max-rent-filter" className="w-[110px] h-9 bg-white border-gray-300 text-gray-900 text-sm">
                  <SelectValue placeholder="No Max" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                  {maxPriceOptions.map(opt => (
                    <SelectItem key={`max-${opt.value || 'none'}`} value={opt.value || "no-max"} className="text-gray-900">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear Filters */}
            {(bedrooms || minRent || maxRent || bathrooms || state || neighborhood) && (
              <>
                <div className="w-px h-6 bg-gray-300" />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearFilters} 
                  data-testid="clear-filters-btn" 
                  className="text-gray-500 hover:text-amber-600 hover:bg-transparent text-sm h-9"
                >
                  Clear
                </Button>
              </>
            )}
            
            {/* Save Search Button */}
            <div className="w-px h-6 bg-gray-300" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSavedSearchModalOpen(true)}
              data-testid="save-search-btn"
              className="h-9 px-4 border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500 flex items-center gap-2"
            >
              <Bell className="w-4 h-4" />
              {savedSearchCount > 0 ? `Alerts (${savedSearchCount})` : 'Save Search'}
            </Button>
          </div>

          {/* Mobile Filter Bar - Light Theme */}
          <div className="md:hidden">
            {/* Quick Access Buttons */}
            <div className="flex gap-2 mb-3">
              {/* Main Filter & Sort Button */}
              <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11 bg-white border-amber-300 text-gray-700 hover:bg-amber-50 hover:border-amber-500"
                  >
                    <SlidersHorizontal className="w-4 h-4 mr-2 text-amber-600" />
                    Filter & Sort
                    {activeFilterCount > 0 && (
                      <Badge className="ml-2 bg-amber-500 text-white text-xs px-1.5 py-0">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="bg-white border-t border-gray-200 rounded-t-2xl h-[85vh]">
                  <SheetHeader className="pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <SheetTitle className="text-gray-900 font-philosopher text-xl">Filter & Sort</SheetTitle>
                      {activeFilterCount > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={clearFilters}
                          className="text-amber-600 hover:text-amber-700 hover:bg-transparent"
                        >
                          Clear all
                        </Button>
                      )}
                    </div>
                  </SheetHeader>
                  
                  <div className="py-6 space-y-6 overflow-y-auto max-h-[calc(85vh-140px)]">
                    {/* Price Range */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-gray-900 font-medium">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                        Price Range
                      </label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Select value={minRent || "no-min"} onValueChange={(val) => setMinRent(val === "no-min" ? "" : val)}>
                            <SelectTrigger className="h-12 bg-white border-gray-300 text-gray-900 text-base">
                              <SelectValue placeholder="No Min" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 max-h-[250px]">
                              {priceOptions.map(opt => (
                                <SelectItem key={`mob-min-${opt.value || 'none'}`} value={opt.value || "no-min"} className="text-gray-900">{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <span className="text-gray-400">to</span>
                        <div className="flex-1">
                          <Select value={maxRent || "no-max"} onValueChange={(val) => setMaxRent(val === "no-max" ? "" : val)}>
                            <SelectTrigger className="h-12 bg-white border-gray-300 text-gray-900 text-base">
                              <SelectValue placeholder="No Max" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 max-h-[250px]">
                              {maxPriceOptions.map(opt => (
                                <SelectItem key={`mob-max-${opt.value || 'none'}`} value={opt.value || "no-max"} className="text-gray-900">{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* Quick Price Presets */}
                      <div className="flex flex-wrap gap-2">
                        {[
                          { label: 'Under $3k', max: '3000' },
                          { label: 'Under $4k', max: '4000' },
                          { label: 'Under $5k', max: '5000' },
                          { label: '$5k+', min: '5000' },
                        ].map((preset) => (
                          <Button
                            key={preset.label}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setMinRent(preset.min || '');
                              setMaxRent(preset.max || '');
                            }}
                            className={`h-8 rounded-full text-xs ${
                              (preset.max && maxRent === preset.max && !minRent) || (preset.min && minRent === preset.min && !maxRent)
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'border-gray-300 text-gray-600 hover:border-amber-500 hover:text-amber-600'
                            }`}
                          >
                            {preset.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Bedrooms */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-gray-900 font-medium">
                        <BedDouble className="w-5 h-5 text-amber-600" />
                        Bedrooms
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { label: 'Any', value: '' },
                          { label: 'Studio', value: '0' },
                          { label: '1 Bed', value: '1' },
                          { label: '2 Beds', value: '2' },
                          { label: '3+ Beds', value: '3' },
                        ].map((option) => (
                          <Button
                            key={option.label}
                            variant="outline"
                            onClick={() => setBedrooms(option.value)}
                            className={`h-11 px-5 rounded-lg ${
                              bedrooms === option.value
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-600'
                            }`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-gray-900 font-medium">
                        <Map className="w-5 h-5 text-amber-600" />
                        Location
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { label: 'All Areas', value: '' },
                          { label: 'New York', value: 'NY' },
                          { label: 'New Jersey', value: 'NJ' },
                          { label: 'Pennsylvania', value: 'PA' },
                        ].map((option) => (
                          <Button
                            key={option.label}
                            variant="outline"
                            onClick={() => { setState(option.value); if (!option.value) setNeighborhood(''); }}
                            className={`h-11 px-5 rounded-lg ${
                              state === option.value
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-600'
                            }`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Neighborhood */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-gray-900 font-medium">
                        <Map className="w-5 h-5 text-amber-600" />
                        Neighborhood
                      </label>
                      <Select value={neighborhood || "any-hood"} onValueChange={(val) => setNeighborhood(val === "any-hood" ? "" : val)}>
                        <SelectTrigger className="h-12 bg-white border-gray-300 text-gray-900 text-base">
                          <SelectValue placeholder="All Neighborhoods" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 max-h-[250px]">
                          <SelectItem value="any-hood" className="text-gray-900">All Neighborhoods</SelectItem>
                          {neighborhoodOptions.map(group => (
                            <React.Fragment key={group.group}>
                              <SelectItem value={`__group_${group.group}`} disabled className="text-xs font-bold text-amber-600 uppercase tracking-wider pt-2">
                                {group.group}
                              </SelectItem>
                              {group.items.map(hood => (
                                <SelectItem key={hood} value={hood} className="text-gray-900 pl-4">{hood}</SelectItem>
                              ))}
                            </React.Fragment>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Bathrooms */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-gray-900 font-medium">
                        <Bath className="w-5 h-5 text-amber-600" />
                        Bathrooms
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { label: 'Any', value: '' },
                          { label: '1 Bath', value: '1' },
                          { label: '1.5 Bath', value: '1.5' },
                          { label: '2+ Baths', value: '2' },
                        ].map((option) => (
                          <Button
                            key={option.label}
                            variant="outline"
                            onClick={() => setBathrooms(option.value)}
                            className={`h-11 px-5 rounded-lg ${
                              bathrooms === option.value
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-600'
                            }`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Apply Button */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
                    <Button 
                      onClick={() => setMobileFilterOpen(false)}
                      className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-base"
                    >
                      Show {units.length} Apartments
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Quick Beds Button */}
              <Select value={bedrooms || "any"} onValueChange={(val) => setBedrooms(val === "any" ? "" : val)}>
                <SelectTrigger className="w-auto h-11 bg-white border-gray-300 text-gray-700 hover:border-amber-500">
                  <BedDouble className="w-4 h-4 mr-1.5 text-amber-600" />
                  <span className="text-sm">{bedrooms === '' ? 'Beds' : bedrooms === '0' ? 'Studio' : `${bedrooms} Bed`}</span>
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="any" className="text-gray-900">Any Beds</SelectItem>
                  <SelectItem value="0" className="text-gray-900">Studio</SelectItem>
                  <SelectItem value="1" className="text-gray-900">1 Bed</SelectItem>
                  <SelectItem value="2" className="text-gray-900">2 Beds</SelectItem>
                  <SelectItem value="3" className="text-gray-900">3+ Beds</SelectItem>
                </SelectContent>
              </Select>

              {/* Quick Area Button */}
              <Select value={state || "any"} onValueChange={(val) => setState(val === "any" ? "" : val)}>
                <SelectTrigger className="w-auto h-11 bg-white border-gray-300 text-gray-700 hover:border-amber-500">
                  <Map className="w-4 h-4 mr-1.5 text-amber-600" />
                  <span className="text-sm">{state || 'Area'}</span>
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="any" className="text-gray-900">All Areas</SelectItem>
                  <SelectItem value="NY" className="text-gray-900">New York</SelectItem>
                  <SelectItem value="NJ" className="text-gray-900">New Jersey</SelectItem>
                  <SelectItem value="PA" className="text-gray-900">Pennsylvania</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Filter Pills - Mobile */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {state && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 px-3 py-1 flex items-center gap-1.5">
                    {state === 'NY' ? 'New York' : state === 'NJ' ? 'New Jersey' : 'Pennsylvania'}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setState('')} />
                  </Badge>
                )}
                {bedrooms && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 px-3 py-1 flex items-center gap-1.5">
                    {bedrooms === '0' ? 'Studio' : `${bedrooms} Bed`}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setBedrooms('')} />
                  </Badge>
                )}
                {(minRent || maxRent) && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 px-3 py-1 flex items-center gap-1.5">
                    {minRent && maxRent ? `$${minRent}-$${maxRent}` : minRent ? `$${minRent}+` : `Under $${maxRent}`}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => { setMinRent(''); setMaxRent(''); }} />
                  </Badge>
                )}
                {bathrooms && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 px-3 py-1 flex items-center gap-1.5">
                    {bathrooms} Bath
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setBathrooms('')} />
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          {/* Quick Filter Chips - Desktop Only - Light Theme */}
          <div className="hidden md:flex flex-wrap items-center gap-2">
            <span className="text-gray-500 text-sm mr-1">Quick filters:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setState('NY'); setMaxRent('4000'); setBedrooms(''); setBathrooms(''); setMinRent(''); setActiveRecommendation(''); }}
              className={`h-8 px-4 rounded-full text-xs font-medium transition-all ${
                state === 'NY' && maxRent === '4000' && !bedrooms
                  ? 'bg-amber-500 text-white border-amber-500' 
                  : 'bg-transparent border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500'
              }`}
            >
              NYC under $4k
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setBedrooms('0'); setState(''); setMinRent(''); setMaxRent(''); setBathrooms(''); setActiveRecommendation(''); }}
              className={`h-8 px-4 rounded-full text-xs font-medium transition-all ${
                bedrooms === '0' && !state && !maxRent
                  ? 'bg-amber-500 text-white border-amber-500' 
                  : 'bg-transparent border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500'
              }`}
            >
              Studios only
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setState('NJ'); setMinRent('3500'); setBedrooms(''); setBathrooms(''); setMaxRent(''); setActiveRecommendation(''); }}
              className={`h-8 px-4 rounded-full text-xs font-medium transition-all ${
                state === 'NJ' && minRent === '3500' && !bedrooms
                  ? 'bg-amber-500 text-white border-amber-500' 
                  : 'bg-transparent border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500'
              }`}
            >
              NJ luxury
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setBedrooms('2'); setState(''); setMinRent(''); setMaxRent(''); setBathrooms(''); setActiveRecommendation(''); }}
              className={`h-8 px-4 rounded-full text-xs font-medium transition-all ${
                bedrooms === '2' && !state && !maxRent
                  ? 'bg-amber-500 text-white border-amber-500' 
                  : 'bg-transparent border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500'
              }`}
            >
              2 Bedrooms
            </Button>
          </div>

          {/* Smart Recommendations - Light Theme */}
          {recommendations.length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/>
                </svg>
                <span className="text-amber-700 text-sm font-medium">Recommended for you</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recommendations.slice(0, 5).map((rec) => (
                  <Button
                    key={rec.id}
                    variant="outline"
                    size="sm"
                    onClick={() => applyRecommendation(rec)}
                    className={`h-9 px-4 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                      activeRecommendation === rec.id
                        ? 'bg-amber-500 text-white border-amber-500' 
                        : 'bg-white border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-600'
                    }`}
                    title={rec.description}
                  >
                    <span className={activeRecommendation === rec.id ? 'text-white' : 'text-amber-600'}>
                      {getRecommendationIcon(rec.icon)}
                    </span>
                    <span>{rec.label}</span>
                    {rec.count > 0 && (
                      <Badge className={`ml-1 text-[10px] px-1.5 py-0 ${
                        activeRecommendation === rec.id 
                          ? 'bg-white/20 text-white' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {rec.count}
                      </Badge>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Trust Strip - Light Theme */}
        <div className="mb-6 py-4 px-6 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>100% verified no-fee buildings</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Updated daily</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span>Direct from owners & landlords</span>
            </div>
          </div>
        </div>

        {/* Results Header with View Toggle - Light Theme */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-xl font-philosopher font-bold text-gray-900">
            {units.length} Apartments Available
          </h2>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              className={viewMode === 'list' 
                ? 'bg-amber-500 text-white hover:bg-amber-600' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-amber-500'}
            >
              <Building2 className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              onClick={() => setViewMode('map')}
              variant={viewMode === 'map' ? 'default' : 'outline'}
              size="sm"
              className={viewMode === 'map' 
                ? 'bg-amber-500 text-white hover:bg-amber-600' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-amber-500'}
            >
              <Map className="w-4 h-4 mr-2" />
              Map
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-xl text-amber-600">Loading apartments...</div>
          </div>
        ) : units.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-xl text-gray-500">No apartments found. Try adjusting your filters.</div>
          </div>
        ) : viewMode === 'map' ? (
          <div className="mb-8">
            <ApartmentMap 
              apartments={units} 
              center={{ lat: 40.7128, lng: -74.0060 }}
              zoom={11}
            />
          </div>
        ) : (
          <div className="space-y-12" data-testid="units-grid">
            {/* Group units into sections of 9 (3x3 grid) */}
            {Array.from({ length: Math.ceil(units.length / 9) }, (_, groupIndex) => {
              const startIndex = groupIndex * 9;
              const groupUnits = units.slice(startIndex, startIndex + 9);
              const isLastGroup = groupIndex === Math.ceil(units.length / 9) - 1;
              
              return (
                <div key={groupIndex} className="relative">
                  {/* Section Header */}
                  {groupIndex > 0 && (
                    <div className="flex items-center gap-4 mb-8">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                      <span className="text-gray-500 text-xs font-philosopher tracking-widest uppercase">
                        {startIndex + 1}–{Math.min(startIndex + 9, units.length)} of {units.length}
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                    </div>
                  )}
                  
                  {/* Cards Grid with increased gap */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {groupUnits.map((unit) => (
                      <ListingCard
                        key={unit.id}
                        unit={unit}
                        user={user}
                        isFavorite={favorites.has(unit.id)}
                        onToggleFavorite={toggleFavorite}
                        onShare={(u) => {
                          setSelectedUnit(u);
                          setShareDialogOpen(true);
                        }}
                        showBlur={!user}
                        theme="light"
                      />
                    ))}
                  </div>
                  
                  {/* Back to Top / Filters button after each group (except last if small) */}
                  {!isLastGroup && (
                    <div className="flex justify-center mt-10">
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="group flex items-center gap-2 px-5 py-2.5 bg-gray-100 border border-gray-300 rounded-full text-sm text-gray-500 hover:text-amber-600 hover:border-amber-400 transition-all duration-300"
                      >
                        <svg 
                          className="w-4 h-4 transform group-hover:-translate-y-0.5 transition-transform" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        Back to Filters
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Final Back to Top button */}
            {units.length > 9 && (
              <div className="flex justify-center pt-4 pb-8">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group flex items-center gap-2 px-6 py-3 bg-amber-50 border border-amber-300 rounded-full text-sm font-medium text-amber-600 hover:bg-amber-100 hover:border-amber-400 transition-all duration-300"
                >
                  <svg 
                    className="w-4 h-4 transform group-hover:-translate-y-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  Back to Top
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Share Dialog */}
      {selectedUnit && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          unit={selectedUnit}
          building={selectedUnit.building}
        />
      )}

      {/* Saved Search Modal */}
      <SavedSearchModal
        open={savedSearchModalOpen}
        onOpenChange={setSavedSearchModalOpen}
        currentFilters={{
          bedrooms,
          minRent,
          maxRent,
          bathrooms,
          state
        }}
        onSearchSaved={fetchSavedSearchCount}
      />
    </div>
  );
};

export default Dashboard;
